import { Link, useNavigate } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Sun,
  Sparkles,
  Heart,
  ShoppingBag,
  GraduationCap,
  Users,
  MessageCircle,
  Wallet as WalletIcon,
  ChevronRight,
} from "lucide-react";

const API = "https://bhavanaastro.onrender.com/api";

// Set to true when real (human) astrologers are live, to show the tile again
const SHOW_CONSULT_TILE = false;

const dashCss = `
  .as-dash { font-family: 'Hind', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif; }
  .as-display { font-family: 'Baloo 2', 'Hind', system-ui, sans-serif; }
  .as-sky {
    background:
      radial-gradient(1.5px 1.5px at 12% 16%, rgba(255,255,255,.7), transparent 60%),
      radial-gradient(1px 1px at 78% 10%, rgba(255,255,255,.6), transparent 60%),
      radial-gradient(1.5px 1.5px at 90% 44%, rgba(255,255,255,.5), transparent 60%),
      radial-gradient(1px 1px at 28% 62%, rgba(255,255,255,.5), transparent 60%),
      radial-gradient(1px 1px at 66% 78%, rgba(255,255,255,.45), transparent 60%),
      radial-gradient(120% 60% at 90% 0%, rgba(255,182,39,.20), transparent 60%),
      #17143F;
  }
`;

const cardShadow =
  "shadow-[0_1px_2px_rgba(23,20,63,0.06),0_10px_24px_-14px_rgba(23,20,63,0.25)]";

const services = [
  { title: "Free Kundli", desc: "Janam kundli turant", icon: Sparkles, link: "/free-kundali" },
  { title: "Matchmaking", desc: "Kundli milan", icon: Heart, link: "/match-making" },
  { title: "Shop", desc: "Rudraksha aur gemstones", icon: ShoppingBag, link: "/shopping" },
  { title: "Courses", desc: "Astrology aur healing", icon: GraduationCap, link: "/occult" },
];
if (SHOW_CONSULT_TILE) {
  services.unshift({
    title: "Expert astrologers",
    desc: "Chat ya call par book karo",
    icon: Users,
    link: "/user/consultancy",
  });
}

export default function UserDashboard() {
  const navigate = useNavigate();

  const storedUser = sessionStorage.getItem("user") || localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const userId = user?.id || user?._id;
  const authHeaders = { headers: { Authorization: `Bearer ${user?.token}` } };
  const firstName = (user?.name || "").trim().split(" ")[0] || "dost";

  const [astrologers, setAstrologers] = useState([]);
  const [loadingAstros, setLoadingAstros] = useState(true);
  const [astroError, setAstroError] = useState(false);

  const [freeAvailable, setFreeAvailable] = useState(null); // null = not known yet
  const [balance, setBalance] = useState(null); // null = not known yet

  const [startingId, setStartingId] = useState(null);
  const [actionError, setActionError] = useState("");

  // Paid chat confirmation
  const [confirmAstro, setConfirmAstro] = useState(null);
  const [confirmError, setConfirmError] = useState("");
  const [paying, setPaying] = useState(false);

  // ---------- data ----------
  const loadAstrologers = useCallback(async () => {
    setLoadingAstros(true);
    setAstroError(false);
    try {
      const res = await axios.get(`${API}/Consult-astrologers?isAI=true&limit=100`, {
        timeout: 30000,
      });
      const ai = (res.data.astrologers || []).filter(
        (a) => a.isAI === true || a.isAI === "true"
      );
      setAstrologers(ai);
    } catch (error) {
      console.error("Error fetching AI astrologers:", error);
      setAstroError(true);
    } finally {
      setLoadingAstros(false);
    }
  }, []);

  useEffect(() => {
    loadAstrologers();
  }, [loadAstrologers]);

  useEffect(() => {
    if (!userId) return;

    axios
      .get(`${API}/consultations/ai/free-status/${userId}`, authHeaders)
      .then((res) => setFreeAvailable(!!res.data.freeAvailable))
      .catch((error) => console.error("Error checking free trial status:", error));

    axios
      .get(`${API}/users/${userId}/details`)
      .then((res) => setBalance(res.data.wallet?.balance ?? 0))
      .catch((error) => console.error("Failed to fetch balance:", error));
  }, [userId]);

  // ---------- actions ----------
  const rateOf = (astro) => astro.rates?.chat ?? 0;

  const rememberPhoto = (data) => {
    if (data?.astrologerPhoto) sessionStorage.setItem("astrologerPhoto", data.astrologerPhoto);
  };

  const handlePick = async (astro) => {
    setActionError("");

    // Free minute: start straight away, no wallet involved
    if (freeAvailable) {
      setStartingId(astro._id);
      try {
        const res = await axios.post(
          `${API}/consultations/ai/start`,
          { userId, astrologerId: astro._id },
          authHeaders
        );
        rememberPhoto(res.data);
        setFreeAvailable(false);
        navigate(`/astrochat/ai/${astro._id}`, {
          state: { isTrial: true, trialSeconds: res.data.trialSeconds || 60 },
        });
      } catch (error) {
        if (error.response?.status === 403) {
          setFreeAvailable(false);
          setActionError("Free chat abhi use ho chuki hai. Ab chat wallet se chalegi.");
        } else {
          setActionError("Chat shuru nahi ho paayi. Dobara try karo.");
        }
      } finally {
        setStartingId(null);
      }
      return;
    }

    // Paid chat: show the price first, then start
    setConfirmError("");
    setConfirmAstro(astro);
  };

  const handleConfirmPaid = async () => {
    if (!confirmAstro) return;
    const rate = rateOf(confirmAstro);
    setPaying(true);
    setConfirmError("");
    try {
      const res = await axios.post(
        `${API}/consultations/ai`,
        { userId, astrologerId: confirmAstro._id, rate },
        authHeaders
      );
      rememberPhoto(res.data);
      navigate(`/astrochat/ai/${confirmAstro._id}`, { state: { isTrial: false } });
    } catch (error) {
      if (error.response?.data?.message === "Insufficient balance") {
        setConfirmError(`Wallet mein balance kam hai. Kam se kam ₹${rate * 5} chahiye.`);
      } else {
        setConfirmError("Chat shuru nahi ho paayi. Dobara try karo.");
      }
    } finally {
      setPaying(false);
    }
  };

  const scrollToList = () => {
    const el = document.getElementById("ai-astrologers");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // ---------- derived ----------
  const confirmRate = confirmAstro ? rateOf(confirmAstro) : 0;
  const confirmCost = confirmRate * 5;
  const notEnough = balance !== null && confirmCost > balance;

  const statusLine =
    freeAvailable === true
      ? "Aapka 1 minute free chat ready hai. Apna AI astrologer chuno aur seedha pooch lo."
      : freeAvailable === false
      ? "Apne AI astrologer se chat karo. Free minute use ho chuka hai, ab chat wallet se chalegi."
      : "Apne AI astrologer se love, career aur marriage ke sawaal pooch lo.";

  return (
    <div className="as-dash min-h-screen bg-[#F5F4FB] text-slate-800">
      <style>{dashCss}</style>

      {/* ---------- 1. Hero: greeting, free-minute status, wallet ---------- */}
      <section className="as-sky text-white">
        <div className="mx-auto grid max-w-6xl gap-5 px-5 pb-8 pt-6 md:grid-cols-[1.4fr_1fr] md:items-center md:gap-10 md:px-8 md:py-10">
          <div>
            <h1 className="as-display text-[30px] font-extrabold leading-[1.1] md:text-[40px]">
              Namaste, {firstName}
            </h1>
            <p className="mt-2 max-w-[38ch] text-[16px] leading-snug text-indigo-100 md:text-[17px]">
              {statusLine}
            </p>
            <button
              type="button"
              onClick={scrollToList}
              className="as-display mt-5 flex w-full items-center justify-center gap-2.5 rounded-2xl bg-[#FFB627] px-6 py-3.5 text-[19px] font-bold text-[#1E1A4D] shadow-[0_10px_28px_-8px_rgba(255,182,39,0.65)] transition hover:bg-[#FFC24D] active:scale-[0.98] md:inline-flex md:w-auto md:px-9"
            >
              <MessageCircle size={22} strokeWidth={2.4} />
              {freeAvailable ? "Free chat shuru karo" : "Astrologer chuno"}
            </button>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/[0.06] p-4">
            <p className="flex items-center gap-2 text-sm text-indigo-200">
              <WalletIcon size={16} /> Wallet balance
            </p>
            <p className="as-display mt-1 text-[34px] font-extrabold leading-none">
              {balance === null ? "..." : `₹${balance}`}
            </p>
            <Link
              to="/user/wallet"
              className="mt-3 inline-flex items-center gap-1 rounded-full border border-[#FFB627]/60 px-4 py-2 text-sm font-semibold text-[#FFB627] transition hover:bg-[#FFB627]/10"
            >
              Paise add karo <ChevronRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ---------- 2. AI astrologers ---------- */}
      <section id="ai-astrologers" className="mx-auto max-w-6xl scroll-mt-24 px-4 pt-8 md:px-8">
        <h2 className="as-display text-[22px] font-bold leading-tight text-[#17143F] md:text-[28px]">
          Apna AI astrologer chuno
        </h2>
        <p className="mt-0.5 text-sm text-slate-500">
          {freeAvailable ? "Kisi par bhi tap karo, free chat shuru." : "Kisi par bhi tap karo, chat shuru."}
        </p>

        {actionError && (
          <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
            {actionError}
          </p>
        )}

        <div className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {loadingAstros &&
            [0, 1, 2].map((i) => (
              <div
                key={i}
                className={`flex animate-pulse items-center gap-3 rounded-2xl bg-white px-3.5 py-3 ${cardShadow}`}
              >
                <div className="h-14 w-14 shrink-0 rounded-full bg-slate-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-32 rounded bg-slate-200" />
                  <div className="h-3 w-44 rounded bg-slate-100" />
                </div>
                <div className="h-9 w-20 rounded-full bg-slate-200" />
              </div>
            ))}

          {!loadingAstros &&
            astrologers.map((astro) => {
              const rate = rateOf(astro);
              const isStarting = startingId === astro._id;
              return (
                <button
                  key={astro._id}
                  type="button"
                  onClick={() => handlePick(astro)}
                  disabled={startingId !== null}
                  className={`flex w-full items-center gap-3 rounded-2xl border border-transparent bg-white px-3.5 py-3 text-left transition hover:border-[#C9C4E8] active:bg-slate-50 disabled:opacity-60 ${cardShadow}`}
                >
                  <div className="relative h-14 w-14 shrink-0">
                    <img
                      src={astro.photo || "/ai.png"}
                      alt={astro.name}
                      loading="lazy"
                      className="h-14 w-14 rounded-full object-cover"
                    />
                    <span className="as-display absolute -bottom-0.5 -right-0.5 rounded-full border-2 border-white bg-[#3A2E9C] px-1.5 text-[10px] font-bold leading-4 text-white">
                      AI
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate text-[16px] font-semibold text-[#17143F]">{astro.name}</h3>
                    <p className="truncate text-[13px] text-slate-500">
                      {astro.systemsKnown?.slice(0, 3).join(", ") || "AI astrological guidance"}
                    </p>
                    <p className="truncate text-[13px] text-slate-400">
                      {astro.languagesKnown?.join(", ")}
                    </p>
                  </div>
                  <span className="shrink-0 rounded-full bg-[#3A2E9C] px-3.5 py-2 text-center text-[13px] font-semibold leading-tight text-white">
                    {isStarting
                      ? "Khul rahi hai..."
                      : freeAvailable
                      ? "Free chat"
                      : rate > 0
                      ? `₹${rate}/min`
                      : "Chat free"}
                  </span>
                </button>
              );
            })}

          {!loadingAstros && astroError && (
            <div className={`col-span-full rounded-2xl bg-white p-5 text-center ${cardShadow}`}>
              <p className="text-sm text-slate-600">
                Astrologers load nahi ho paaye. Thodi der baad dobara try karo.
              </p>
              <button
                type="button"
                onClick={loadAstrologers}
                className="mt-3 rounded-full bg-[#3A2E9C] px-6 py-2.5 text-sm font-semibold text-white"
              >
                Dobara try karo
              </button>
            </div>
          )}

          {!loadingAstros && !astroError && astrologers.length === 0 && (
            <div className={`col-span-full rounded-2xl bg-white p-5 text-center text-sm text-slate-600 ${cardShadow}`}>
              Abhi koi AI astrologer available nahi hai.
            </div>
          )}
        </div>
      </section>

      {/* ---------- 3. Daily horoscope + other services ---------- */}
      <section className="mx-auto max-w-6xl px-4 pb-12 pt-9 md:px-8">
        <Link
          to="/horoscopes"
          className={`flex items-center gap-4 rounded-2xl border border-[#FFB627]/50 bg-white p-4 transition hover:border-[#FFB627] ${cardShadow}`}
        >
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-[#FFF3D6] text-[#B87400]">
            <Sun size={24} />
          </span>
          <span className="min-w-0 flex-1">
            <span className="as-display block text-[19px] font-bold leading-tight text-[#17143F]">
              Aaj ka rashifal
            </span>
            <span className="block text-[14px] text-slate-500">
              Apni rashi ka aaj ka rashifal padho
            </span>
          </span>
          <ChevronRight size={20} className="shrink-0 text-slate-400" />
        </Link>

        <h2 className="as-display mt-8 text-[22px] font-bold leading-tight text-[#17143F] md:text-[28px]">
          Aur bhi services
        </h2>
        <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3">
          {services.map((s, i) => {
            const Icon = s.icon;
            const isLastOdd = services.length % 2 === 1 && i === services.length - 1;
            return (
              <Link
                key={s.title}
                to={s.link}
                className={`flex items-center gap-3 rounded-2xl border border-[#DAD6F0] bg-white p-3.5 transition hover:border-[#3A2E9C] active:bg-[#EEEBFA] ${
                  isLastOdd ? "col-span-2 md:col-span-1" : ""
                }`}
              >
                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-[#EEEBFA] text-[#3A2E9C]">
                  <Icon size={20} />
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-[15px] font-semibold text-[#17143F]">{s.title}</span>
                  <span className="block truncate text-[13px] text-slate-500">{s.desc}</span>
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ---------- Paid chat confirmation ---------- */}
      {confirmAstro && (
        <div
          className="fixed inset-0 z-[60] flex items-end justify-center bg-black/50 sm:items-center"
          onClick={() => !paying && setConfirmAstro(null)}
        >
          <div
            className="w-full max-w-md rounded-t-3xl bg-white p-5 sm:rounded-3xl"
            style={{ paddingBottom: "max(1.25rem, env(safe-area-inset-bottom))" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <img
                src={confirmAstro.photo || "/ai.png"}
                alt={confirmAstro.name}
                className="h-12 w-12 rounded-full object-cover"
              />
              <div className="min-w-0">
                <p className="as-display truncate text-[19px] font-bold leading-tight text-[#17143F]">
                  {confirmAstro.name}
                </p>
                <p className="text-sm text-slate-500">AI astrologer</p>
              </div>
            </div>

            {confirmRate > 0 ? (
              <p className="mt-4 text-[15px] leading-snug text-slate-700">
                Chat shuru karne par <strong>₹{confirmCost}</strong> (5 minute, ₹{confirmRate}/min) aapke wallet se
                katenge.
                {balance !== null && <> Aapka balance: <strong>₹{balance}</strong>.</>}
              </p>
            ) : (
              <p className="mt-4 text-[15px] leading-snug text-slate-700">Yeh chat free hai.</p>
            )}

            {(notEnough || confirmError) && (
              <p className="mt-3 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
                {confirmError || `Wallet mein balance kam hai. Kam se kam ₹${confirmCost} chahiye.`}
              </p>
            )}

            {notEnough ? (
              <Link
                to="/user/wallet"
                className="as-display mt-4 flex w-full items-center justify-center rounded-2xl bg-[#FFB627] px-6 py-3.5 text-[18px] font-bold text-[#1E1A4D]"
              >
                Wallet recharge karo
              </Link>
            ) : (
              <button
                type="button"
                onClick={handleConfirmPaid}
                disabled={paying}
                className="as-display mt-4 flex w-full items-center justify-center rounded-2xl bg-[#FFB627] px-6 py-3.5 text-[18px] font-bold text-[#1E1A4D] transition hover:bg-[#FFC24D] disabled:opacity-60"
              >
                {paying ? "Chat khul rahi hai..." : "Chat shuru karo"}
              </button>
            )}

            <button
              type="button"
              onClick={() => setConfirmAstro(null)}
              disabled={paying}
              className="mt-2 block w-full py-2 text-center text-sm font-medium text-[#3A2E9C]"
            >
              Rehne do
            </button>
          </div>
        </div>
      )}
    </div>
  );
}